import { useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import { Inbox, BarChart3, Search, Users, LogOut, Loader2 } from "lucide-react";
import { api, ADMIN_BASE } from "./api";
import Login from "./Login";
import Leads from "./Leads";
import Numbers from "./Numbers";
import SeoPanel from "./SeoPanel";
import Team from "./Team";

const TABS = [
  { to: ADMIN_BASE, label: "Leads", icon: Inbox, end: true },
  { to: `${ADMIN_BASE}/numbers`, label: "Numbers", icon: BarChart3 },
  { to: `${ADMIN_BASE}/seo`, label: "SEO", icon: Search },
  { to: `${ADMIN_BASE}/team`, label: "Team", icon: Users },
];

const useNoIndex = () => {
  useEffect(() => {
    document.title = "Back office | Good Home Improvement";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow"; m.id = "admin-noindex";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
};

const Shell = ({ user, onLogout, children }) => (
  <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="admin-shell">
    <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950 text-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="font-display text-lg font-black uppercase leading-none">Good Home <span className="text-slate-400">Back office</span></p>
          <p className="truncate text-xs text-slate-400" data-testid="admin-user-name">{user.name} · {user.email}</p>
        </div>
        <button onClick={onLogout} className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-slate-700 px-3 py-2 text-sm font-semibold hover:bg-slate-800" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
      <nav className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-2 pb-2">
        {TABS.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`admin-tab-${label.toLowerCase()}`}
            className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-white text-slate-950" : "text-slate-300 hover:bg-slate-800"}`}>
            <Icon className="h-4 w-4" />{label}
          </NavLink>
        ))}
      </nav>
    </header>
    <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
  </div>
);

export default function AdminApp() {
  useNoIndex();
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() =>
      api.post("/auth/refresh").then((r) => setUser(r.data)).catch(() => setUser(false)));
  }, []);
  if (user === null) return <div className="grid min-h-screen place-items-center bg-slate-100"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };
  return (
    <Shell user={user} onLogout={logout}>
      <Routes>
        <Route index element={<Leads />} />
        <Route path="numbers" element={<Numbers />} />
        <Route path="seo" element={<SeoPanel />} />
        <Route path="team" element={<Team me={user} />} />
        <Route path="*" element={<Leads />} />
      </Routes>
    </Shell>
  );
}
